// Push Notification Service
// Handles push notification registration, subscription, and management

export interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  tag?: string;
  data?: any;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
  requireInteraction?: boolean;
  silent?: boolean;
  vibrate?: number[];
  sound?: string;
}

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  expirationTime?: number;
}

export interface NotificationPermission {
  granted: boolean;
  denied: boolean;
  default: boolean;
  canRequest: boolean;
}

export class PushNotificationService {
  private swRegistration: ServiceWorkerRegistration | null = null;
  private pushSubscription: PushSubscription | null = null;
  private notificationPermission: NotificationPermission;
  private isSupported: boolean;
  private serverPublicKey: string = 'BEl62iUYgUivxIkv69yViEuiBIa1HI8Hwlb7tcrSoBBlnXkqGcFubq1v8OH2OOosFfLa3O18aK0h+JQJAgw0XaMs'; // VAPID public key

  constructor() {
    this.isSupported = this.checkSupport();
    this.notificationPermission = this.getPermissionStatus();
    this.init();
  }

  /**
   * Check if push notifications are supported
   */
  private checkSupport(): boolean {
    return 'serviceWorker' in navigator && 
           'PushManager' in window && 
           'Notification' in window;
  }

  /**
   * Get current notification permission status
   */
  private getPermissionStatus(): NotificationPermission {
    if (!this.isSupported) {
      return {
        granted: false,
        denied: false,
        default: false,
        canRequest: false
      };
    }

    const permission = Notification.permission;
    return {
      granted: permission === 'granted',
      denied: permission === 'denied',
      default: permission === 'default',
      canRequest: permission === 'default'
    };
  }

  /**
   * Initialize the push notification service
   */
  private async init(): Promise<void> {
    if (!this.isSupported) {
      console.warn('Push notifications are not supported in this browser');
      return;
    }

    try {
      // Register service worker if not already registered
      this.swRegistration = await navigator.serviceWorker.ready;
      
      // Check for existing push subscription
      await this.checkExistingSubscription();
      
      console.log('Push notification service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize push notification service:', error);
    }
  }

  /**
   * Check if user already has a push subscription
   */
  private async checkExistingSubscription(): Promise<void> {
    if (!this.swRegistration) return;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        this.pushSubscription = subscription;
        console.log('Existing push subscription found');
      }
    } catch (error) {
      console.error('Error checking existing subscription:', error);
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    if (this.notificationPermission.granted) {
      return true;
    }

    if (this.notificationPermission.denied) {
      throw new Error('Notification permission has been denied');
    }

    try {
      const permission = await Notification.requestPermission();
      this.notificationPermission = this.getPermissionStatus();
      
      if (permission === 'granted') {
        console.log('Notification permission granted');
        return true;
      } else {
        console.log('Notification permission denied');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe(): Promise<PushSubscription | null> {
    if (!this.isSupported) {
      throw new Error('Push notifications are not supported');
    }

    if (!this.notificationPermission.granted) {
      const granted = await this.requestPermission();
      if (!granted) {
        throw new Error('Notification permission required');
      }
    }

    if (!this.swRegistration) {
      throw new Error('Service worker not ready');
    }

    try {
      // Convert VAPID public key to Uint8Array
      const vapidPublicKey = this.urlBase64ToUint8Array(this.serverPublicKey);
      
      // Subscribe to push notifications
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      });

      this.pushSubscription = subscription;
      console.log('Successfully subscribed to push notifications');
      
      // Send subscription to server
      await this.sendSubscriptionToServer(subscription);
      
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.pushSubscription) {
      return true;
    }

    try {
      await this.pushSubscription.unsubscribe();
      this.pushSubscription = null;
      
      // Notify server about unsubscription
      await this.removeSubscriptionFromServer();
      
      console.log('Successfully unsubscribed from push notifications');
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }

  /**
   * Send subscription data to server
   */
  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    try {
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')!),
              auth: this.arrayBufferToBase64(subscription.getKey('auth')!)
            }
          },
          userAgent: navigator.userAgent,
          platform: navigator.platform
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      console.log('Subscription sent to server successfully');
    } catch (error) {
      console.error('Error sending subscription to server:', error);
      throw error;
    }
  }

  /**
   * Remove subscription from server
   */
  private async removeSubscriptionFromServer(): Promise<void> {
    try {
      const response = await fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      console.log('Unsubscription sent to server successfully');
    } catch (error) {
      console.error('Error sending unsubscription to server:', error);
    }
  }

  /**
   * Show local notification
   */
  async showNotification(payload: PushNotificationPayload): Promise<Notification | null> {
    if (!this.notificationPermission.granted) {
      throw new Error('Notification permission not granted');
    }

    try {
      const notification = new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/assets/icons/android-icon-192x192.png',
        badge: payload.badge || '/assets/icons/android-icon-72x72.png',
        // image: payload.image, // Not supported in all browsers
        tag: payload.tag,
        data: payload.data,
        // actions: payload.actions, // Not supported in all browsers
        requireInteraction: payload.requireInteraction || false,
        silent: payload.silent || false,
        // vibrate: payload.vibrate || [200, 100, 200], // Not supported in all browsers
        // sound: payload.sound // Not supported in all browsers
      });

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        this.handleNotificationClick(notification, payload);
      };

      // Handle notification close
      notification.onclose = () => {
        this.handleNotificationClose(notification, payload);
      };

      // Handle notification action clicks
      if (payload.actions) {
        // Note: onactionclick is not widely supported, using onclick instead
        // notification.onactionclick = (event) => {
        //   this.handleNotificationAction(notification, payload, event.action);
        // };
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Handle notification click
   */
  private handleNotificationClick(notification: Notification, payload: PushNotificationPayload): void {
    notification.close();
    
    // Focus the app window
    if (window.focus) {
      window.focus();
    }

    // Handle custom click logic
    if (payload.data?.url) {
      window.open(payload.data.url, '_blank');
    } else if (payload.data?.route) {
      // Navigate to specific route in the app
      window.location.href = payload.data.route;
    }

    // Emit custom event for app handling
    window.dispatchEvent(new CustomEvent('notificationClick', {
      detail: { notification, payload }
    }));
  }

  /**
   * Handle notification close
   */
  private handleNotificationClose(notification: Notification, payload: PushNotificationPayload): void {
    // Emit custom event for app handling
    window.dispatchEvent(new CustomEvent('notificationClose', {
      detail: { notification, payload }
    }));
  }

  /**
   * Handle notification action
   */
  private handleNotificationAction(notification: Notification, payload: PushNotificationPayload, action: string): void {
    notification.close();
    
    // Emit custom event for app handling
    window.dispatchEvent(new CustomEvent('notificationAction', {
      detail: { notification, payload, action }
    }));
  }

  /**
   * Get current subscription status
   */
  getSubscriptionStatus(): {
    subscribed: boolean;
    permission: NotificationPermission;
    supported: boolean;
  } {
    return {
      subscribed: !!this.pushSubscription,
      permission: this.notificationPermission,
      supported: this.isSupported
    };
  }

  /**
   * Get current subscription data
   */
  getSubscriptionData(): PushSubscriptionData | null {
    if (!this.pushSubscription) return null;

    return {
      endpoint: this.pushSubscription.endpoint,
      keys: {
        p256dh: this.arrayBufferToBase64(this.pushSubscription.getKey('p256dh')!),
        auth: this.arrayBufferToBase64(this.pushSubscription.getKey('auth')!)
      },
      expirationTime: this.pushSubscription.expirationTime || undefined
    };
  }

  /**
   * Test push notification
   */
  async testNotification(): Promise<void> {
    await this.showNotification({
      title: 'Test Notification',
      body: 'This is a test push notification from the app',
      icon: '/assets/icons/android-icon-192x192.png',
      tag: 'test',
      data: {
        url: window.location.href,
        timestamp: new Date().toISOString()
      },
      actions: [
        {
          action: 'open',
          title: 'Open App'
        },
        {
          action: 'dismiss',
          title: 'Dismiss'
        }
      ]
    });
  }

  /**
   * Convert VAPID public key from base64 to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

// Create singleton instance
export const pushNotificationService = new PushNotificationService();
